import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import highchartsHeatmap from 'highcharts/modules/heatmap';
import highchartsBoost from 'highcharts/modules/boost';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { compare_name_asc } from '../../utils/utils';

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
highchartsHeatmap(Highcharts);
highchartsBoost(Highcharts);
noDataToDisplay(Highcharts);

dayjs.extend(dayOfYear);

type Props = {
  data: any;
  rain_dates: any;
};

const CompareCleaningSchedules = ({ data, rain_dates }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);
  const chartComponent = useRef(null);
  const isLoading = false;

  // Memoize sorted data
  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort(compare_name_asc);
  }, [data]);

  // Memoize categories
  const categories = useMemo(() => {
    return data?.[0]?.data?.daily?.times || [];
  }, [data]);

  // Memoize prepared series data
  const preparedData = useMemo(() => {
    if (!data || !categories.length) return null;

    let timeseries_data: any[][] = [];
    let rain_data: any[][] = [];

    sortedData.forEach((item: any, y: number) => {
      const days_to_clean = Math.max(parseInt(item.time_to_clean), 1);

      // Use cleaning_graph if available
      if (item?.cleaning_graph && Array.isArray(item.cleaning_graph)) {
        item.cleaning_graph.forEach((value: number, index: number) => {
          if (value !== 0) {
            // value 1 or 2 = cleaning (alternating colors), value 3 = blacklist
            timeseries_data.push([index, y, value]);
          }
        });
      } else {
        // Fallback to old schedule-based approach if cleaning_graph not available
        item?.schedule.forEach((date: string, index: number) => {
          let d = new Date(Date.parse(date));

          // Try exact match first
          let startIndex = categories.findIndex((catDate: any) => catDate === date);

          // If not found, try to find the closest date
          if (startIndex === -1) {
            const targetTime = d.getTime();
            let closestIndex = -1;
            let minDiff = Infinity;

            categories.forEach((catDate: any, catIndex: any) => {
              const catTime = new Date(catDate).getTime();
              const diff = Math.abs(catTime - targetTime);
              if (diff < minDiff) {
                minDiff = diff;
                closestIndex = catIndex;
              }
            });

            // Only use if the difference is less than 7 days (1 week)
            if (minDiff < 7 * 24 * 60 * 60 * 1000) {
              startIndex = closestIndex;
            }
          }

          if (startIndex === -1) return; // Skip if date not found in categories

          const isEvenCleaning = index % 2 === 0;
          const cleaningValue = isEvenCleaning ? 1 : 0.5;

          for (let i = 0; i < days_to_clean; i++) {
            timeseries_data.push([startIndex + i, y, cleaningValue]);
          }
        });
      }

      rain_dates?.forEach((date: string) => {
        let d = new Date(Date.parse(date));

        // Try exact match first
        let rainIndex = categories.findIndex((catDate: any) => catDate === date);

        // If not found, try to find the closest date
        if (rainIndex === -1) {
          const targetTime = d.getTime();
          let closestIndex = -1;
          let minDiff = Infinity;

          categories.forEach((catDate: any, catIndex: any) => {
            const catTime = new Date(catDate).getTime();
            const diff = Math.abs(catTime - targetTime);
            if (diff < minDiff) {
              minDiff = diff;
              closestIndex = catIndex;
            }
          });

          // Only use if the difference is less than 7 days (1 week)
          if (minDiff < 7 * 24 * 60 * 60 * 1000) {
            rainIndex = closestIndex;
          }
        }

        if (rainIndex !== -1) {
          rain_data.push([rainIndex, y, 5]); // Use value 5 for rain to avoid conflict with cleaning values
        }
      });
      // Fix heatmap that it shows the first and last day properly. If 1st and last index have no data put a null point.
      if (rain_data.findIndex((d) => d[0] === 0) === -1) rain_data.push([0, 0, null]);
      if (rain_data.findIndex((d) => d[0] === categories.length - 1) === -1) rain_data.push([categories.length - 1, 0, null]);
    });

    return [
      {
        name: 'Cleaning',
        data: timeseries_data,
        color: 'orange',
      },
      {
        name: 'Rain',
        data: rain_data,
        color: 'blue',
      },
    ];
  }, [data, rain_dates, categories, sortedData]);

  useEffect(() => {
    if (preparedData) {
      prepareHighcharts(preparedData);
    }
  }, [preparedData, isDarkMode, sortedData]);

  const prepareHighcharts = (highChartsData: any) => {
    const chart: Highcharts.Options = {
      chart: {
        type: 'heatmap',
        height: 170 + 25 * data?.length,
        marginTop: 50,
        marginBottom: 120,
        // @ts-expect-error Highcharts type missing zoomType in this setup
        zoomType: 'x',
      },
      title: {
        text: 'Cleaning Schedules',
        align: 'left',
      },
      // Enable boost module for better performance with large datasets
      boost: {
        useGPUTranslations: true,
        usePreallocated: true,
      },
      legend: {
        enabled: true,
        itemMarginBottom: -20,
      },
      xAxis: {
        type: 'datetime',
        categories: categories,
        title: { text: 'Date' },
        min: 0,
        max: (categories?.length || 365) - 1,
        labels: {
          formatter: function (): string {
            // @ts-expect-error Highcharts formatter context
            let d = new Date(Date.parse(this.value));

            // Show year when multiple solutions are selected
            if (data && data.length > 1) {
              return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
            }

            // For single solution, show day/month only
            return d.getDate() + '/' + ('0' + (d.getMonth() + 1)).slice(-2);
          },
        },
        gridLineWidth: 1,
      },
      yAxis: {
        categories: sortedData.map((item: any) => item.name),
        title: { text: '' },
      },
      tooltip: {
        formatter: function (): string {
          // @ts-ignore - Highcharts formatter context
          const point = this.point;
          // @ts-ignore - Highcharts formatter context
          const series = this.series;
          let d = new Date(Date.parse(series.xAxis.categories[point.x]));

          // Show year in tooltip when multiple solutions are selected
          const dateFormat =
            data && data.length > 1
              ? d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
              : d.getDate() + '/' + ('0' + (d.getMonth() + 1)).slice(-2);

          // Check the value to determine the event type
          let eventType = series.name;
          if (point.value === 3) {
            eventType = 'Blacklist';
          } else if (point.value === 4) {
            eventType = 'Special Cleaning';
          }

          return series.yAxis.categories[point.y || 0] + '<br>' + eventType + ' on ' + dateFormat;
        },
      },
      colorAxis: {
        min: 0,
        max: 5,
        stops: [
          [0, '#FFFFFF'], // 0 = white (no data)
          [0.2, '#FF6B35'], // 1 = orange (cleaning - odd)
          [0.4, '#C55824'], // 2 = darker orange/brown (cleaning - even)
          [0.6, '#808080'], // 3 = gray (blacklist days)
          [0.8, '#b75cf8ff'], // 4 = dark purple (special cleaning)
          [1, '#0066CC'], // 5 = blue (rain)
        ],
        showInLegend: false, // Hide the color gradient legend
      },
      // legend moved to top
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          // Enable turboThreshold for large datasets
          turboThreshold: 10000,
          // Reduce animation time
          animation: {
            duration: 500,
          },
        },
        heatmap: {
          // Optimize heatmap rendering
          enableMouseTracking: true,
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          data && (
            <>
              <HighchartsReact
                ref={chartComponent}
                containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
                highcharts={Highcharts}
                options={chartOptions}
              />
            </>
          )
        )}
      </Box>
    </>
  );
};

export default CompareCleaningSchedules;
